import { SearchPageChrome } from "@/components/search/page-chrome"
import {
  Building,
  CircleCheck,
  Download,
  FileUser,
  Heart,
  LampDesk,
  LucideIcon,
  ShieldCheck,
  Smile,
  Trophy,
  User2,
  Users,
  Video,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { PageBody } from "@/components/ui/layout"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { convertUrl } from "@/services/utility"
import { Button } from "@/components/ui/button"
import {
  Accordion,
  AccordionContent,
  AccordionTrigger,
  AccordionItem,
} from "@/components/ui/accordion"
import { Badge } from "@/components/ui/badge"
import { Progress } from "@/components/ui/progress"

import { addEducatorToShortlist, useShowTeacherProps } from "./page-props"
import { Resources } from "i18next"
import { ActionButton } from "@/components/shared/action-buttons"
import { ShortlistMembership } from "@/components/search/shortlists/shortlist-membership"
import { EmbeddedSheetPage, SheetPageTrigger } from "@/components/ui/sheet-page"
import {
  EducatorReliefListingSheet,
  SchoolReliefListingSheet,
} from "./relief-listing-sheets"

export default function Page() {
  const { teacher, shortlists } = useShowTeacherProps()

  return (
    <>
      <SearchPageChrome
        identity={{ name: `${teacher.first_name} ${teacher.last_name}` }}
        action={<Actions />}
      />
      <PageBody>
        <ShortlistMembership shortlists={shortlists} />
        <Card>
          <CardContent className="grid h-full w-full grid-cols-1 gap-6 p-6 lg:grid-cols-[2.5fr_3fr_3fr]">
            <TeacherInfo />

            <div className="flex flex-col gap-6">
              <BackgroundVideo />
              <Accreditation />
              <TeacherExperience />
            </div>
            <div className="flex flex-col gap-6">
              <TeacherMethod />
              <PersonalStyle />
            </div>
          </CardContent>
        </Card>
      </PageBody>
    </>
  )
}

// Each action is offered when the page's capabilities say the user may take it;
// a relief invitation also needs the teacher to be open to invitations.
function Actions() {
  const { teacher_profile_uuid, relief_invitable_token, capabilities } =
    useShowTeacherProps()
  const { t } = useTranslation("search")

  return (
    <>
      {capabilities.can_invite_to_team && (
        <EmbeddedSheetPage
          href={`/educators/invitations/new`}
          data={{ teacher_profile_uuid }}
        >
          <SheetPageTrigger asChild>
            <ActionButton Icon={Users} variant="default">
              {t("show.teacher.invite")}
            </ActionButton>
          </SheetPageTrigger>
        </EmbeddedSheetPage>
      )}
      {relief_invitable_token &&
        capabilities.educator_relief_invitation_href && (
          <EducatorReliefListingSheet
            href={capabilities.educator_relief_invitation_href}
            relief_invitable_token={relief_invitable_token}
            listing_profile_uuid={teacher_profile_uuid}
          />
        )}
      {relief_invitable_token && capabilities.school_relief_invitation_href && (
        <SchoolReliefListingSheet
          href={capabilities.school_relief_invitation_href}
          relief_invitable_token={relief_invitable_token}
          listing_profile_uuid={teacher_profile_uuid}
        />
      )}
      {capabilities.can_shortlist && (
        <EmbeddedSheetPage
          href={`/search/educators/${teacher_profile_uuid}/shortlist_entries/new`}
          scope={addEducatorToShortlist}
        >
          <SheetPageTrigger asChild>
            <ActionButton Icon={Heart} variant="solid">
              {t("show.shortlist")}
            </ActionButton>
          </SheetPageTrigger>
        </EmbeddedSheetPage>
      )}
    </>
  )
}

function TeacherInfo() {
  const { teacher } = useShowTeacherProps()
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-4">
        <div className="flex w-full flex-col items-center gap-4">
          <Avatar className="size-45">
            <AvatarImage src={teacher.photo_id_url || undefined} />
            <AvatarFallback>
              {teacher.first_name[0]}
              {teacher.last_name[0]}
            </AvatarFallback>
          </Avatar>
          <span className="text-2xl font-semibold">
            {teacher.first_name} {teacher.last_name}
          </span>

          <BasicInfo />
        </div>
      </CardContent>
    </Card>
  )
}

function BasicInfo() {
  const { teacher } = useShowTeacherProps()
  const { t } = useTranslation("search", { keyPrefix: "show" })
  const { t: tLanguages } = useTranslation("languages", {
    keyPrefix: "languages",
  })
  const { t: tGenders } = useTranslation("common", { keyPrefix: "user.gender" })
  const { t: tCountries } = useTranslation("countries", {
    keyPrefix: "by_sacc_code",
  })
  const { t: tReligions } = useTranslation("religions", {
    keyPrefix: "religions",
  })

  return (
    <div className="flex w-full flex-col gap-4">
      <Attribute
        label={t("teacher.primary_language")}
        value={
          teacher.primary_language_key
            ? tLanguages(
                teacher.primary_language_key as keyof Resources["languages"]["languages"],
              )
            : t("missing_attribute")
        }
      />
      <Attribute
        label={t("teacher.secondary_language")}
        value={
          teacher.secondary_language_key
            ? tLanguages(
                teacher.secondary_language_key as keyof Resources["languages"]["languages"],
              )
            : t("missing_attribute")
        }
      />
      <Attribute
        label={t("teacher.gender")}
        value={
          teacher.gender_key
            ? tGenders(
                teacher.gender_key as keyof Resources["common"]["user"]["gender"],
              )
            : t("missing_attribute")
        }
      />

      <Attribute
        label={t("teacher.citizenships")}
        value={
          teacher.citizenship_country_sacc_codes.length > 0
            ? teacher.citizenship_country_sacc_codes.map((saccCode) =>
                tCountries(
                  saccCode as keyof Resources["countries"]["by_sacc_code"],
                ),
              )
            : t("missing_attribute")
        }
      />

      <Attribute
        label={t("teacher.origin_country")}
        value={
          teacher.origin_country_sacc_code
            ? tCountries(
                teacher.origin_country_sacc_code as keyof Resources["countries"]["by_sacc_code"],
              )
            : t("missing_attribute")
        }
      />

      <Attribute
        label={t("teacher.religion")}
        value={
          teacher.religion_key
            ? tReligions(
                teacher.religion_key as keyof Resources["religions"]["religions"],
              )
            : t("missing_attribute")
        }
      />

      <Attribute label={t("teacher.mobile")} value={teacher.mobile ?? "N/A"} />
    </div>
  )
}

function BackgroundVideo() {
  const { teacher } = useShowTeacherProps()
  const { t } = useTranslation("search", { keyPrefix: "show.teacher" })
  return (
    <Card>
      <CardHeader>
        <IconCardTitle Icon={Video}>
          {t("background_video.title")}
        </IconCardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-center gap-4">
        {teacher.background_video_url ? (
          <iframe
            width="260"
            src={convertUrl(teacher.background_video_url)}
            allowFullScreen
          ></iframe>
        ) : (
          <p>{t("background_video.empty")}</p>
        )}
      </CardContent>
    </Card>
  )
}

function Accreditation() {
  const { teacher } = useShowTeacherProps()
  const { accreditation } = teacher
  const { t: tCurricula } = useTranslation("subjects", {
    keyPrefix: "curricula",
  })
  const { t } = useTranslation("search", {
    keyPrefix: "show.teacher.accreditations",
  })
  const otherApprovals = accreditation.other_approvals
  return (
    <Card>
      <CardHeader>
        <IconCardTitle Icon={ShieldCheck}>{t("title")}</IconCardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {accreditation.state_approvals.length > 0 && (
          <div className="contents">
            <span>{t("subsections.state_approvals")}</span>
            {accreditation.state_approvals.map((stateName) => (
              <div
                key={stateName}
                className="ml-4 flex flex-row items-center gap-2 text-sm"
              >
                <CircleCheck size={16} className="text-brand-sagecool-blue" />
                {tCurricula(
                  stateName as keyof Resources["subjects"]["curricula"],
                )}
              </div>
            ))}
          </div>
        )}
        {otherApprovals.length > 0 && (
          <div className="contents">
            <span>{t("subsections.other_accreditations")}</span>
            {otherApprovals.map((description, index) => (
              <div
                key={index}
                className="ml-4 flex flex-row items-center gap-2 text-sm"
              >
                <CircleCheck size={16} className="text-brand-sagecool-blue" />
                {description}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function TeacherExperience() {
  const { teacher } = useShowTeacherProps()
  const { t } = useTranslation("search", {
    keyPrefix: "show.teacher.experience",
  })
  const { t: tSubjects } = useTranslation("subjects", { keyPrefix: "subjects" })
  const { t: tLanguages } = useTranslation("languages", {
    keyPrefix: "languages",
  })
  const { t: tGenders } = useTranslation("common", { keyPrefix: "user.gender" })
  const { t: tReligions } = useTranslation("religions", {
    keyPrefix: "religions",
  })
  const { t: tDepressions } = useTranslation("student_attributes_options", {
    keyPrefix: "depression_options",
  })
  const { t: tAdhd } = useTranslation("student_attributes_options", {
    keyPrefix: "adhd_options",
  })

  return (
    <Card>
      <CardHeader>
        <IconCardTitle Icon={FileUser}>{t("title")}</IconCardTitle>
      </CardHeader>
      <CardContent>
        {teacher.experience.resume_url && (
          <Button
            className="w-full"
            onClick={() => {
              window.open(
                teacher.experience.resume_url,
                "_blank",
                "noopener,noreferrer",
              )
            }}
          >
            <Download />
            {t("resume")}
          </Button>
        )}
        <Accordion type="multiple">
          {teacher.experience?.teaching_experiences && (
            <AccordionItem value="teachingExperiences">
              <AccordionTrigger>
                {t("subsections.teaching_experience")}
              </AccordionTrigger>
              <AccordionContent className="flex flex-col gap-4">
                {(teacher.experience?.teaching_experiences ?? []).map(
                  ({ subject, institution, from_year, to_year }, index) => (
                    <div>
                      <div className="uppercase">{subject}</div>
                      <div
                        className="text-muted-foreground flex flex-row gap-2 text-xs"
                        key={index}
                      >
                        <div> - {institution}</div>
                        <div className="ml-auto italic">
                          ({from_year} - {to_year})
                        </div>
                      </div>
                    </div>
                  ),
                )}
              </AccordionContent>
            </AccordionItem>
          )}
          {(teacher.experience?.education_qualifications ?? []) && (
            <AccordionItem value="educationQualification">
              <AccordionTrigger>
                {t("subsections.education_qualifications")}
              </AccordionTrigger>
              <AccordionContent className="flex flex-col gap-4">
                {(teacher.experience?.education_qualifications ?? []).map(
                  (item, index) => (
                    <div key={index}>
                      <div className="uppercase">{item.qualification}</div>
                      <div className="flex flex-row gap-2 text-xs">
                        <div className="text-muted-foreground">
                          {" "}
                          - {item.university_name}
                        </div>
                        <div className="text-muted-foreground ml-auto italic">
                          ({item.year_obtained})
                        </div>
                      </div>
                      {item.awards && (
                        <div className="text-muted-foreground flex flex-row items-center gap-2 text-xs">
                          <Trophy size="16" />
                          <div>{item.awards}</div>
                        </div>
                      )}
                    </div>
                  ),
                )}
              </AccordionContent>
            </AccordionItem>
          )}
          {(teacher.experience?.work_experiences ?? []) && (
            <AccordionItem value="workExperiences">
              <AccordionTrigger>
                {t("subsections.work_experience")}
              </AccordionTrigger>
              <AccordionContent className="flex flex-col gap-4">
                {(teacher.experience?.work_experiences ?? []).map(
                  (item, index) => (
                    <div className="flex flex-col gap-2" key={index}>
                      <div>
                        {item.title} ({item.field_of_expertise})
                      </div>
                      <div className="text-muted-foreground flex flex-col gap-1 text-xs italic">
                        <div className="flex flex-row justify-between">
                          <div className="flex flex-row gap-2">
                            <Building size={16} /> {item.company_name}
                          </div>
                          ({item.from_year} - {item.to_year})
                        </div>
                        {item.referee && (
                          <div className="flex flex-row gap-2">
                            <User2 size={16} /> {item.referee}
                          </div>
                        )}
                        {item.description && <div>{item.description}</div>}
                      </div>
                    </div>
                  ),
                )}
              </AccordionContent>
            </AccordionItem>
          )}
          {(teacher.experience?.assessor_subjects ?? []).length > 0 && (
            <AccordionItem value="assessorExperiences">
              <AccordionTrigger>
                {t("subsections.assessor_experience")}
              </AccordionTrigger>
              <AccordionContent className="flex flex-col gap-4">
                {(teacher.experience?.assessor_subjects ?? []).map((item) => (
                  <div className="flex flex-col gap-2 text-xs">
                    <div className="uppercase">
                      {tSubjects(
                        item.subject_key as keyof Resources["subjects"]["subjects"],
                      )}
                    </div>
                    <div className="text-muted-foreground">
                      {item.from_year} - {item.to_year}
                    </div>
                  </div>
                ))}
              </AccordionContent>
            </AccordionItem>
          )}
          {(teacher.experience?.examiner_subjects ?? []).length > 0 && (
            <AccordionItem value="assessorExperiences">
              <AccordionTrigger>
                {t("subsections.examiner_experience")}
              </AccordionTrigger>
              <AccordionContent className="flex flex-col gap-4">
                {(teacher.experience?.examiner_subjects ?? []).map((item) => (
                  <div className="flex flex-col gap-2 text-xs">
                    <div className="uppercase">
                      {tSubjects(
                        item.subject_key as keyof Resources["subjects"]["subjects"],
                      )}
                    </div>
                    <div className="text-muted-foreground">
                      {item.from_year} - {item.to_year}
                    </div>
                  </div>
                ))}
              </AccordionContent>
            </AccordionItem>
          )}
          {teacher.experience?.students_taught && (
            <AccordionItem value="studentsTaught">
              <AccordionTrigger>
                {t("subsections.students_taught")}
              </AccordionTrigger>
              <AccordionContent className="flex flex-col gap-4">
                {[
                  {
                    label: t("students_taught.subsections.genders"),
                    data: teacher.experience?.students_taught.gender_keys.map(
                      (key) =>
                        tGenders(
                          key as keyof Resources["common"]["user"]["gender"],
                        ),
                    ),
                    comment: teacher.experience?.students_taught.gender_notes,
                  },
                  {
                    label: t("students_taught.subsections.primary_languages"),
                    data: teacher.experience?.students_taught.primary_language_keys.map(
                      (key) =>
                        tLanguages(
                          key as keyof Resources["languages"]["languages"],
                        ),
                    ),
                    comment:
                      teacher.experience?.students_taught
                        .primary_language_notes,
                  },
                  {
                    label: t("students_taught.subsections.secondary_languages"),
                    data: teacher.experience?.students_taught.secondary_language_keys.map(
                      (key) =>
                        tLanguages(
                          key as keyof Resources["languages"]["languages"],
                        ),
                    ),
                    comment:
                      teacher.experience?.students_taught
                        .secondary_language_notes,
                  },
                  {
                    label: t("students_taught.subsections.depressions"),
                    data: teacher.experience?.students_taught.depressions.map(
                      (item: string) =>
                        tDepressions(
                          item as keyof Resources["student_attributes_options"]["depression_options"],
                        ),
                    ),
                    comment:
                      teacher.experience?.students_taught.depression_notes,
                  },
                  {
                    label: t("students_taught.subsections.adhds"),
                    data: teacher.experience?.students_taught.adhds.map(
                      (item: string) =>
                        tAdhd(
                          item as keyof Resources["student_attributes_options"]["adhd_options"],
                        ),
                    ),
                    comment: teacher.experience?.students_taught.adhd_notes,
                  },
                  {
                    label: t("students_taught.subsections.religions"),
                    data: teacher.experience?.students_taught.religion_keys.map(
                      (item: string) =>
                        tReligions(
                          item as keyof Resources["religions"]["religions"],
                        ),
                    ),
                    comment: teacher.experience?.students_taught.religion_note,
                  },
                  {
                    label: t(
                      "students_taught.subsections.intellectual_ability",
                    ),
                    data: undefined,
                    comment:
                      teacher.experience?.students_taught
                        .intellectual_ability_notes,
                  },
                  {
                    label: t("students_taught.subsections.socioeconomic"),
                    data: undefined,
                    comment:
                      teacher.experience?.students_taught.socioeconomic_notes,
                  },
                  {
                    label: t("students_taught.subsections.other"),
                    data: undefined,
                    comment: teacher.experience?.students_taught.other_notes,
                  },
                ].map((item, index) => (
                  <div className="flex flex-col gap-2" key={index}>
                    <div className="uppercase">{item.label}</div>
                    <div className="text-muted-foreground flex flex-col text-xs">
                      {(item?.data ?? []).map(
                        (data: string | undefined, index: number) =>
                          data && <div key={index}>- {data}</div>,
                      )}
                    </div>
                    <div className="text-muted-foreground text-xs italic">
                      {item.comment}
                    </div>
                  </div>
                ))}
              </AccordionContent>
            </AccordionItem>
          )}
        </Accordion>
      </CardContent>
    </Card>
  )
}

function TeacherMethod() {
  const { teacher } = useShowTeacherProps()
  const { t } = useTranslation("search", {
    keyPrefix: "show.teacher.teaching_methods",
  })
  return (
    <Card>
      <CardHeader>
        <IconCardTitle Icon={LampDesk}>{t("title")}</IconCardTitle>
      </CardHeader>
      <CardContent>
        <div>{t("subsections.teaching_technology")}</div>
        <div className="flex flex-row flex-wrap gap-2">
          {teacher.teaching_technology_names.map((name, index) => (
            <Badge key={index}>{name}</Badge>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

const PersonalStyle = () => {
  const { teacher } = useShowTeacherProps()
  const { t } = useTranslation("search", {
    keyPrefix: "show.teacher.personal_style",
  })
  return (
    <Card>
      <CardHeader>
        <IconCardTitle Icon={Smile}>{t("title")}</IconCardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-4">
          <div>
            <span>{t("subsections.style_autocratic")}</span>
            <Progress
              value={(teacher.teacher_rating?.style_autocratic || 0) * 10}
            />
          </div>
          <div>
            <span>{t("subsections.style_reserved")}</span>
            <Progress
              value={(teacher.teacher_rating?.style_reserved || 0) * 10}
            />
          </div>
          <div>
            <span>{t("subsections.style_talk")}</span>
            <Progress value={(teacher.teacher_rating?.style_talk || 0) * 10} />
          </div>
          <div>
            <span>{t("subsections.style_consultative")}</span>
            <Progress
              value={(teacher.teacher_rating?.style_consultative || 0) * 10}
            />
          </div>
          <div>
            <span>{t("subsections.style_participative")}</span>
            <Progress
              value={(teacher.teacher_rating?.style_participative || 0) * 10}
            />
          </div>

          {teacher.teacher_rating?.style_comment && (
            <span>" {teacher.teacher_rating?.style_comment} "</span>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function Attribute({
  label,
  value,
}: {
  label: string
  value: string | string[]
}) {
  return (
    <div className="flex flex-row flex-wrap justify-between">
      <div className="text-muted-foreground">{label}</div>
      <div>{Array.isArray(value) ? value.join(", ") : value}</div>
    </div>
  )
}

function IconCardTitle({
  children,
  Icon,
}: {
  children: React.ReactNode
  Icon: LucideIcon
}) {
  return (
    <CardTitle className="flex flex-row gap-2">
      <Icon />
      {children}
    </CardTitle>
  )
}
